/**
 * App-wide toast helper. Thin wrapper over `sonner` so callers never import it
 * directly - keeps the dependency swappable and lets us inject our error
 * parser (`extractApiError`) at one boundary.
 *
 * Usage:
 *   toast.success('Booking created')
 *   toast.error('Failed to cancel booking')        // string
 *   toast.error(err)                               // any thrown value
 *   toast.critical(err)                            // sticky, no auto-dismiss
 *   toast.promise(mutation.mutateAsync(data), {
 *     loading: 'Saving…',
 *     success: 'Saved',
 *     error: (e) => extractApiError(e).detail,
 *   })
 */
import { toast as sonner, type ExternalToast } from 'sonner'

import { extractApiError } from './error'

type Input = string | unknown

function detailOf(input: Input): string {
  return typeof input === 'string' ? input : extractApiError(input).detail
}

// QA F2: one failed request showed two toasts, the MutationCache default
// (server detail) plus a call-site `onError` toasting axios's own
// `err.message` ("Request failed with status code 400"). That generic
// sentence carries nothing the server detail lacks, so it is dropped when an
// error toast went up a moment ago, and identical messages collapse into one.
const GENERIC_AXIOS = /^(Request failed with status code \d{3}|Network Error|timeout of \d+ms exceeded)$/
const DEDUPE_WINDOW_MS = 1500
let lastErrorAt = 0

/** True when this error toast would only repeat one just shown. Exported for tests. */
export function isRedundantError(detail: string, now: number, lastAt: number): boolean {
  return GENERIC_AXIOS.test(detail.trim()) && now - lastAt < DEDUPE_WINDOW_MS
}

function showError(input: Input, opts?: ExternalToast) {
  const detail = detailOf(input)
  const now = Date.now()
  if (isRedundantError(detail, now, lastErrorAt)) return undefined
  lastErrorAt = now
  return sonner.error(detail, { id: `error:${detail}`, ...opts })
}

export const toast = {
  success: (msg: string, opts?: ExternalToast) => sonner.success(msg, opts),

  info: (msg: string, opts?: ExternalToast) => sonner.info(msg, opts),

  warning: (msg: string, opts?: ExternalToast) => sonner.warning(msg, opts),

  /**
   * Auto-dismissing error toast. Pass a string for static copy or any thrown
   * value (axios error, plain Error, etc.) and we'll extract a clean sentence.
   */
  error: (input: Input, opts?: ExternalToast) => showError(input, opts),

  /**
   * Sticky error toast - does not auto-dismiss. Use for payment failures,
   * destructive-op 5xx, or anything the user must explicitly acknowledge.
   * Supply an `action` (label + onClick) for retry flows.
   */
  critical: (input: Input, opts?: ExternalToast) =>
    sonner.error(detailOf(input), { duration: Infinity, ...opts }),

  promise: sonner.promise,
  dismiss: sonner.dismiss,
  custom: sonner.custom,
}

export type { ExternalToast } from 'sonner'
